import { TopBarTalentSearchFiltersHubProps } from '@type/filters';
import { useTranslation } from 'next-i18next';
import { useDebounce } from '@hooks/useDebounce/useDebounce';
import { useFiltersHub } from '@hooks/filters/useFiltersHub';
import { TopBarLocations } from '@components/features/filters/partials/TopBarLocations';
import { OrderByBoardType } from '@components/features/filters/partials/OrderByBoardType';

import {
  OpenSidebarButton,
  OrderByGroup,
  TopBarGroup,
  TopBarSingle,
  TopBarLanguages,
  TopBarSearchIn,
  TopBarConfineResults,
  ApplicationStats,
  TopBarSearchAlerts,
  TopBarSuggestedFilters,
} from '../partials';
import {
  domains, careerLevels, studyLevels, lastActivity,
} from '../helpers/filterObjects';


/**
 * @description The hub for the top bar company filters. It contains all the filters for the top bar and also the
 * filters for the sidebar (via OpenFiltersGroup component).
 * * **queryParams** - the query params for the filters.
 * * **setQueryParams** - the function to set the query params.
 * * **toggleDrawer** - the function to toggle the drawer.
 * * **applicationStats** - the application stats (viewed, interview, new, etc.) for the job applicants detail page; optional.
 * * **jobSlug** - the job slug (for applicants list); optional.
 * * **locale** - the locale of the app.
 * * **jobBasicInfo** - the basic info for the job (for applicants list); optional.
 */
export const TopBarTalentSearchFiltersHub = (props: TopBarTalentSearchFiltersHubProps) => {
  // Destructure props
  const {
    queryParams,
    setQueryParams,
    toggleDrawer,
    applicationStats,
    jobSlug,
    hideApplicationNumbers,
    locale,
    jobBasicInfo,
    boardInfo,
    hideSidebarButton,
    hideOrderBy,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Form id to associate popover inputs rendered in a portal with this form
  const formId = 'topbarFiltersForm';

  // Check if we have filters
  const filtersKeys = queryParams ? Object.keys(queryParams) : [];
  const hasFilters = filtersKeys.length > 0;

  // Show the border between filters and the open sidebar button if there are filters but not for 'sort'
  const showBorder = hasFilters
    && !(filtersKeys.length === 1 && (queryParams?.sort || queryParams?.status));

  const confineKeys = [
    'searchInApplicants',
    'searchInSavedApplicants',
    'driversLicence',
    'recommendedByAgent',
    'excludeContacted',
    // include this only if the checkbox is mounted
    ...(jobBasicInfo ? ['passedThroughTheInterview'] : []),
    'videoInterviewOnly',
  ];

  const orderKeys = hideOrderBy ? ['status'] : ['sort', 'status'];

  const allTopBarKeys = [
    ...confineKeys,
    // Location
    'location', 'radiusInKm',
    // Search In
    'nameSearch', 'queryFields', 'presentExperience',
    // Last Activity
    'lastActivity',
    // Languages
    'spokenLanguages',
    // Career / Domains / Studies
    'careerLevels', 'domains', 'studyLevels', 'studySpecialization', 'graduateStart', 'graduateEnd',
    // Order / Status (applicants page)
    ...orderKeys,
  ];

  const externalFields = ['keyword', 'profileCategory', ...(hideOrderBy ? ['sort'] : [])];


  // FILTERS HUB HOOK
  // ***********************************************
  const {
    formRef, handleStep, record, setFormValues, updateFormElements, submitForm,
  } = useFiltersHub({
    queryParams,
    setQueryParams,
    externalFieldsList: externalFields,
    internalFieldsList: allTopBarKeys,
  });

  // Debounced submit to avoid cross-form races for toggles
  const submitDebounced = useDebounce(() => {
    submitForm();
  }, 150);

  // Return component
  // ***********************************************
  return (
    <form id={formId} noValidate onSubmit={handleStep} ref={formRef} className="relative flex items-center gap-2">

      {/* APPLICATION STATS group - for applicants page */
        applicationStats && (
          <ApplicationStats
            data={applicationStats}
            jobSlug={jobSlug}
            hideApplicationNumbers={hideApplicationNumbers}
            recordFunc={record}
            submitFunc={submitForm}
            idPrefix="topBar_"
            queryParam={queryParams?.status}
          />
        )
      }

      {
        boardInfo?.isFromBoard && (
          <OrderByBoardType
            recordFunc={record}
            totalCandidates={boardInfo?.totalCandidates}
            submitFunc={submitForm}
            idPrefix="topBar_"
          />
        )
      }

      {/* ORDER BY group */}
      {!hideOrderBy && (
        <OrderByGroup
          recordFunc={record}
          submitFunc={submitForm}
          idPrefix="topBar_"
          jobSlug={jobSlug}
        />
      )}

      {/* OPEN SIDEBAR FILTERS button */}
      {!hideSidebarButton && (
        <div {...showBorder ? { className: 'pr-2 border-r border-input/60' } : {}}>
          <OpenSidebarButton
            toggleDrawer={toggleDrawer}
          />
        </div>
      )}

      {/* RESUME SEARCH ALERTS (spread) - initial landing when no filters are applied and not in job context */
        !jobSlug && !hasFilters && (
          <TopBarSearchAlerts
            locale={locale}
            setQueryParams={setQueryParams}
          />
        )
      }

      {/* SUGGESTED FILTERS group; default activateForParams = ['keyword', 'similarSearch'] (it shows only for these)  */
        <TopBarSuggestedFilters
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          jobSlug={jobSlug}
          locale={locale}
        />
      }

      {/* LOCATION group */
        queryParams?.location && (
          <TopBarLocations
            submitFunc={submitForm}
            recordFunc={record}
            queryParams={queryParams}
            clearGroup={() => setFormValues({ location: '', radiusInKm: '' })}
            idPrefix="topBar_"
          />
        )
      }

      {/* SEARCH IN group */
        (queryParams?.nameSearch
          || queryParams?.queryFields
          || queryParams?.presentExperience)
        && (
          <TopBarSearchIn
            submitFunc={submitForm}
            recordFunc={record}
            updateFormElements={updateFormElements}
            updateFormValues={() => setFormValues(queryParams)}
            clearGroup={() => setFormValues({
              nameSearch: '', queryFields: '', presentExperience: '',
            })}
            queryParams={queryParams}
            idPrefix="topBar_"
          />
        )
      }

      {/* CONFINE RESULTS group */
        (queryParams?.searchInApplicants
          || queryParams?.videoInterviewOnly
          || queryParams?.searchInSavedApplicants
          || queryParams?.driversLicence
          || queryParams?.recommendedByAgent
          || queryParams?.excludeContacted
          || queryParams?.passedThroughTheInterview)
        && (
          <TopBarConfineResults
            submitFunc={submitDebounced}
            recordFunc={record}
            updateFormElements={updateFormElements}
            clearGroup={() => setFormValues({
              searchInApplicants: '',
              videoInterviewOnly: '',
              searchInSavedApplicants: '',
              driversLicence: '',
              recommendedByAgent: '',
              excludeContacted: '',
              passedThroughTheInterview: '',
            })}
            queryParams={queryParams}
            idPrefix="topBar_"
            jobBasicInfo={jobBasicInfo}
            formId={formId}
          />
        )
      }

      {/* LAST ACTIVE group */
        queryParams?.lastActivity && (
          <TopBarGroup
            data={lastActivity.fields}
            fieldName="lastActivity"
            idPrefix="topBar_"
            filterGroupName="global.active-in.label"
            panelHelperText="global.active-in.label"
            clearGroup={() => setFormValues({ lastActivity: '' })}
            submitFunc={submitForm}
            recordFunc={record}
            recordOptions={{ convertValue: 'toNumber' }}
            queryParam={queryParams.lastActivity.toString()}
            iconType="radio"
          />
        )
      }

      {/* SPOKEN LANGUAGES group */
        queryParams?.spokenLanguages && (
          <TopBarLanguages
            submitFunc={submitForm}
            recordFunc={record}
            clearGroup={() => setFormValues({ spokenLanguages: '' })}
            idPrefix="topBar_"
          />
        )
      }

      {/* CAREER LEVELS group */
        queryParams?.careerLevels && (
          <TopBarGroup
            data={careerLevels.fields}
            fieldName="careerLevels"
            idPrefix="topBar_"
            filterGroupName="global.experience-level.label"
            panelHelperText="global.experience-level.label"
            multiple
            clearGroup={() => setFormValues({ careerLevels: '' })}
            submitFunc={submitForm}
            recordFunc={record}
            recordOptions={{ convertValue: 'commaStringToArray' }}
            queryParam={queryParams.careerLevels.toString()}
          />
        )
      }

      {/* DOMAINS group */
        queryParams?.domains && (
          <TopBarGroup
            data={domains.fields}
            fieldName="domains"
            idPrefix="topBar_"
            filterGroupName="global.filter.domain"
            panelHelperText="global.filter.domain"
            multiple
            clearGroup={() => setFormValues({ domains: '' })}
            submitFunc={submitForm}
            recordFunc={record}
            recordOptions={{ convertValue: 'commaStringToArray' }}
            queryParam={queryParams.domains.toString()}
          />
        )
      }

      {/* STUDY LEVELS group */
        queryParams?.studyLevels && (
          <TopBarGroup
            data={studyLevels.fields}
            fieldName="studyLevels"
            idPrefix="topBar_"
            filterGroupName="global.studies.label"
            panelHelperText="global.studies.label"
            multiple
            clearGroup={() => setFormValues({ studyLevels: '' })}
            submitFunc={submitForm}
            recordFunc={record}
            recordOptions={{ convertValue: 'commaStringToArray' }}
            queryParam={queryParams.studyLevels.toString()}
          />
        )
      }

      {/* STUDY SPECIALIZATION group */
        queryParams?.studySpecialization && (
          <TopBarSingle
            filterName="studySpecialization"
            filterLabel={`${t('study.faculty.label')}: ${queryParams?.studySpecialization}`}
            recordFunc={record}
            submitFunc={submitForm}
            setFormValues={setFormValues}
            primaryValue={queryParams.studySpecialization}
          />
        )
      }

      {/* STUDY START DATE group */
        queryParams?.graduateStart && (
          <TopBarSingle
            filterName="graduateStart"
            filterLabel={`${t('filters.studies.studies-start')}: ${queryParams.graduateStart}`}
            recordFunc={record}
            submitFunc={submitForm}
            setFormValues={setFormValues}
            primaryValue={queryParams.graduateStart}
          />
        )
      }

      {/* STUDY END DATE group */
        queryParams?.graduateEnd && (
          <TopBarSingle
            filterName="graduateEnd"
            filterLabel={`${t('filters.studies.studies-end')}: ${queryParams.graduateEnd}`}
            recordFunc={record}
            submitFunc={submitForm}
            setFormValues={setFormValues}
            primaryValue={queryParams.graduateEnd}
          />
        )
      }
    </form>
  );
};
